/**
 * Admin Logic Module
 * Centralise la logique métier pour les fonctionnalités admin :
 * - SMTP Settings
 * - Fintecture Credentials & Payments
 * - Company Images
 * - Drive Search (admin)
 */

import { Api } from "~/api/Api"
import { request } from "~/api/core/request"
import type { SMTPSettingsResource } from "~/api"

class AdminLogicClass {
    /**
     * Configuration pour les appels à request()
     */
    private readonly config = {
        BASE: import.meta.env.VITE_LARAVEL + "/",
    }

    // ========================================
    // SMTP Settings
    // ========================================

    /**
     * Récupère les paramètres SMTP d'une entreprise
     */
    async getSmtpSettings(
        customerId: string | number,
    ): Promise<SMTPSettingsResource | null> {
        try {
            return await Api.smtpSettings.adminSmtpShow(String(customerId))
        } catch {
            return null
        }
    }

    /**
     * Crée les paramètres SMTP
     */
    async createSmtpSettings(data: {
        customer_id: string
        host: string
        port: string
        username: string
        password: string
        encryption: string
    }): Promise<SMTPSettingsResource> {
        return request(this.config, {
            method: "POST",
            url: "admin/smtp",
            body: data,
        })
    }

    /**
     * Met à jour les paramètres SMTP
     */
    async updateSmtpSettings(
        id: string,
        data: {
            host: string
            port: string
            username: string
            password: string
            encryption: string
        },
    ): Promise<SMTPSettingsResource> {
        return request(this.config, {
            method: "PUT",
            url: `admin/smtp/${id}`,
            body: data,
        })
    }

    /**
     * Supprime les paramètres SMTP (revenir au défaut)
     */
    async deleteSmtpSettings(id: string): Promise<void> {
        return request(this.config, {
            method: "DELETE",
            url: `admin/smtp/${id}`,
        })
    }

    // ========================================
    // Fintecture Credentials
    // ========================================

    /**
     * Récupère les credentials Fintecture d'une entreprise
     */
    async getFintectureCredentials(
        customerId: string | number,
    ): Promise<any | null> {
        try {
            return await request(this.config, {
                method: "GET",
                url: `admin/fintecture?customer_id=${customerId}`,
            })
        } catch {
            return null
        }
    }

    /**
     * Crée les credentials Fintecture
     */
    async createFintectureCredentials(data: {
        customer_id: string
        app_id: string
        app_id_secret: string
        app_priv_key: string
        env: string
        agence_id?: number | null
    }): Promise<any> {
        return request(this.config, {
            method: "POST",
            url: "admin/fintecture",
            body: data,
        })
    }

    /**
     * Met à jour les credentials Fintecture
     */
    async updateFintectureCredentials(
        id: string,
        data: {
            app_id: string
            app_id_secret: string
            /** Absente : la cle enregistree est conservee. */
            app_priv_key?: string
            env: string
            agence_id?: number | null
        },
    ): Promise<any> {
        return request(this.config, {
            method: "PUT",
            url: `admin/fintecture/${id}`,
            body: data,
        })
    }

    /**
     * Teste les identifiants Fintecture enregistres (jeton, puis requete signee), sans creer de paiement.
     * `key_proven` n'est vrai qu'en production : en sandbox Fintecture ne verifie pas la signature.
     */
    async testFintectureCredentials(id: string | number): Promise<{
        success: boolean
        env: string
        key_proven: boolean
        steps: { key: string; ok: boolean; message: string }[]
    }> {
        return request(this.config, {
            method: "POST",
            url: `admin/fintecture/${id}/test`,
        })
    }

    /**
     * Supprime les credentials Fintecture
     */
    async deleteFintectureCredentials(id: string): Promise<void> {
        return request(this.config, {
            method: "DELETE",
            url: `admin/fintecture/${id}`,
        })
    }

    // ========================================
    // Fintecture Payments History
    // ========================================

    /**
     * Récupère l'historique des paiements Fintecture d'une entreprise
     */
    async getFintecturePayments(customerId: string | number): Promise<{
        data: any[]
        current_page: number
        last_page: number
        total: number
    }> {
        return request(this.config, {
            method: "GET",
            url: `admin/invoice-payments?customer_id=${customerId}`,
        })
    }

    // ========================================
    // Company Images
    // ========================================

    /**
     * Récupère toutes les images d'une entreprise
     */
    async getCompanyImages(customerId: string | number): Promise<{
        background: string | null
        logo: string | null
        entete: string | null
        icon: string | null
    }> {
        return request(this.config, {
            method: "GET",
            url: `admin/company/${customerId}/images`,
        })
    }

    /**
     * Upload une image d'entreprise
     */
    async uploadCompanyImage(
        customerId: string | number,
        file: File,
        type: "background" | "logo" | "entete" | "icon",
    ): Promise<any> {
        const formData = new FormData()
        formData.append("image", file)
        formData.append("type", type)

        return request(this.config, {
            method: "POST",
            url: `admin/company/${customerId}/images`,
            body: formData,
        })
    }

    /**
     * Supprime une image d'entreprise
     */
    async deleteCompanyImage(
        customerId: string | number,
        type: "background" | "logo" | "entete" | "icon",
    ): Promise<{ message: string }> {
        return request(this.config, {
            method: "DELETE",
            url: `admin/company/${customerId}/images?type=${type}`,
        })
    }

    // ========================================
    // Suivi Client (Devis Consultations)
    // ========================================

    /**
     * Récupère la liste des devis consultés pour une entreprise
     */
    async getDevisConsultations(customerId: string | number): Promise<any[]> {
        return request(this.config, {
            method: "GET",
            url: `admin/devis-consultations?customer_id=${customerId}`,
        })
    }

    /**
     * Récupère le détail d'un devis consulté
     */
    async getDevisConsultationDetail(
        customerId: string | number,
        idDevis: string | number,
    ): Promise<{
        devis: any
        consultations: any[]
        signature_links: any[]
        timeline: any[]
        stats: {
            total_consultations: number
            total_links_created: number
            has_active_link: boolean
        }
    }> {
        return request(this.config, {
            method: "GET",
            url: `admin/devis-consultations/${idDevis}?customer_id=${customerId}`,
        })
    }

    /**
     * Créer un lien de signature pour un devis
     */
    async createSignatureLink(
        customerId: string | number,
        idDevis: string | number,
    ): Promise<{
        token: string
        url: string
        status: string
        created_at: string
    }> {
        return request(this.config, {
            method: "POST",
            url: `admin/devis-consultations/${idDevis}/signature-link?customer_id=${customerId}`,
        })
    }

    /**
     * Révoquer les liens de signature actifs pour un devis
     */
    async revokeSignatureLinks(
        customerId: string | number,
        idDevis: string | number,
    ): Promise<{
        message: string
        revoked_count: number
    }> {
        return request(this.config, {
            method: "DELETE",
            url: `admin/devis-consultations/${idDevis}/signature-link?customer_id=${customerId}`,
        })
    }

    // ========================================
    // Prodevis Import Logs
    // ========================================

    /**
     * Récupère la liste des imports Prodevis (paginée)
     */
    async getProdevisImports(params?: {
        customer_id?: string | number
        status?: string
        page?: number
    }): Promise<{
        data: any[]
        current_page: number
        last_page: number
        total: number
        per_page: number
    }> {
        const queryParts: string[] = []
        if (params?.customer_id)
            queryParts.push(`customer_id=${params.customer_id}`)
        if (params?.status) queryParts.push(`status=${params.status}`)
        if (params?.page) queryParts.push(`page=${params.page}`)
        const qs = queryParts.length ? `?${queryParts.join("&")}` : ""

        return request(this.config, {
            method: "GET",
            url: `admin/prodevis-imports${qs}`,
        })
    }

    /**
     * Relance un import échoué
     */
    async retryProdevisImport(id: number): Promise<{
        success: boolean
        message: string
    }> {
        return request(this.config, {
            method: "POST",
            url: `admin/prodevis-imports/${id}/retry`,
        })
    }

    // ========================================
    // Drive Admin Search
    // ========================================

    /**
     * Recherche des fichiers pour une entreprise (admin)
     */
    async searchOrphanFiles(customerId: string | number): Promise<{
        data: any[]
        total: number
    }> {
        return request(this.config, {
            method: "POST",
            url: "admin/drive/orphans",
            body: { customer_id: customerId },
        })
    }

    async searchDriveFiles(params: {
        customer_id: string | number
        display_name?: string
        entity_type?: string
        entity_id?: number
        tag?: string
        page?: number
    }): Promise<{
        data: any[]
        meta?: {
            current_page: number
            last_page: number
            total: number
        }
    }> {
        return request(this.config, {
            method: "POST",
            url: "admin/drive/search",
            body: params,
        })
    }

    // ========================================
    // Drive Orphan Files
    // ========================================

    /**
     * Liste les fichiers orphelins (dans MinIO mais pas dans drive_files)
     */
    async getOrphanFiles(customerId: string | number): Promise<{
        data: Array<{
            file_name: string
            size: number
            last_modified: string
            url: string
        }>
        total: number
    }> {
        return request(this.config, {
            method: "POST",
            url: "admin/drive/orphans",
            body: { customer_id: customerId },
        })
    }

    /**
     * Supprime un fichier orphelin du bucket MinIO
     */
    async deleteOrphanFile(
        customerId: string | number,
        fileName: string,
    ): Promise<{ message: string }> {
        return request(this.config, {
            method: "DELETE",
            url: "admin/drive/orphans",
            body: { customer_id: customerId, file_name: fileName },
        })
    }

    /**
     * Référence un fichier orphelin dans drive_files
     */
    async registerOrphanFile(
        customerId: string | number,
        data: {
            file_name: string
            display_name: string
            author_type?: string | null
            author_id?: number | null
            attachments?: Array<{ entity_type: string; entity_id: number }>
            tags?: string[]
        },
    ): Promise<any> {
        return request(this.config, {
            method: "POST",
            url: "admin/drive/orphans/register",
            body: { customer_id: customerId, ...data },
        })
    }

    /**
     * Met à jour les métadonnées d'un fichier drive
     */
    async updateDriveFile(
        fileId: number,
        data: {
            display_name?: string
            author_type?: string | null
            author_id?: number | null
            attachments?: Array<{ entity_type: string; entity_id: number }>
            tags?: string[]
        },
    ): Promise<any> {
        return request(this.config, {
            method: "PUT",
            url: `admin/drive/${fileId}`,
            body: data,
        })
    }

    // ========================================
    // Telescope
    // ========================================

    async getTelescopeEntries(params?: {
        type?: string
        company_id?: number
        status_code?: string
        date_from?: string
        date_to?: string
        search?: string
        per_page?: number
        page?: number
    }): Promise<{
        data: any[]
        current_page: number
        last_page: number
        total: number
        per_page: number
    }> {
        const queryParts: string[] = []
        if (params?.type) queryParts.push(`type=${params.type}`)
        if (params?.company_id)
            queryParts.push(`company_id=${params.company_id}`)
        if (params?.status_code)
            queryParts.push(`status_code=${params.status_code}`)
        if (params?.date_from) queryParts.push(`date_from=${params.date_from}`)
        if (params?.date_to) queryParts.push(`date_to=${params.date_to}`)
        if (params?.search)
            queryParts.push(`search=${encodeURIComponent(params.search)}`)
        if (params?.per_page) queryParts.push(`per_page=${params.per_page}`)
        if (params?.page) queryParts.push(`page=${params.page}`)
        const qs = queryParts.length ? `?${queryParts.join("&")}` : ""

        return request(this.config, {
            method: "GET",
            url: `admin/telescope/entries${qs}`,
        })
    }

    async getTelescopeEntry(uuid: string): Promise<any> {
        return request(this.config, {
            method: "GET",
            url: `admin/telescope/entries/${uuid}`,
        })
    }

    async getTelescopeTypes(): Promise<Array<{ type: string; count: number }>> {
        return request(this.config, {
            method: "GET",
            url: `admin/telescope/types`,
        })
    }

    async clearTelescopeEntries(params?: {
        before?: string
        type?: string
    }): Promise<{ deleted: number }> {
        const queryParts: string[] = []
        if (params?.before) queryParts.push(`before=${params.before}`)
        if (params?.type) queryParts.push(`type=${params.type}`)
        const qs = queryParts.length ? `?${queryParts.join("&")}` : ""

        return request(this.config, {
            method: "DELETE",
            url: `admin/telescope/entries${qs}`,
        })
    }

    // ========================================
    // Company CGV
    // ========================================

    /**
     * Récupère le CGV actif d'une entreprise
     */
    async getCompanyCgv(customerId: string | number): Promise<any | null> {
        try {
            return await request(this.config, {
                method: "GET",
                url: `admin/company/${customerId}/cgv`,
            })
        } catch {
            return null
        }
    }

    /**
     * Upload un PDF CGV pour une entreprise
     */
    async uploadCompanyCgv(
        customerId: string | number,
        file: File,
    ): Promise<any> {
        const formData = new FormData()
        formData.append("file", file)

        return request(this.config, {
            method: "POST",
            url: `admin/company/${customerId}/cgv`,
            body: formData,
        })
    }

    /**
     * Supprime un CGV
     */
    async deleteCompanyCgv(
        customerId: string | number,
        fileId: number,
    ): Promise<void> {
        return request(this.config, {
            method: "DELETE",
            url: `admin/company/${customerId}/cgv/${fileId}`,
        })
    }

    // ========================================
    // Fiche entreprise (section General) et diagnostics
    // ========================================

    /** Serveurs d'application auxquels une entreprise peut etre rattachee. */
    async getWebservices(): Promise<WebserviceOption[]> {
        return request(this.config, { method: "GET", url: "admin/webservices" })
    }

    async getCompanySettings(
        customerId: string | number,
    ): Promise<CompanySettings> {
        return request(this.config, {
            method: "GET",
            url: `admin/company/${customerId}/settings`,
        })
    }

    /** Cree une entreprise (inactive par defaut). */
    async createCompanySettings(
        data: CompanySettingsInput,
    ): Promise<CompanySettings> {
        return request(this.config, {
            method: "POST",
            url: "admin/company-settings",
            body: data,
        })
    }

    async updateCompanySettings(
        customerId: string | number,
        data: CompanySettingsInput,
    ): Promise<CompanySettings> {
        return request(this.config, {
            method: "PUT",
            url: `admin/company/${customerId}/settings`,
            body: data,
        })
    }

    /** Diagnostic de liaison Lola : port du serveur, puis requete via le webservice. */
    async lolaCheck(customerId: string | number): Promise<any> {
        return request(this.config, {
            method: "GET",
            url: `admin/company/${customerId}/lola-check`,
        })
    }

    /** Teste la connexion Prodevis (parametres saisis, ou ceux enregistres pour un champ vide). */
    async prodevisTest(
        customerId: string | number,
        data: {
            base?: string
            port?: number | null
            login?: string
            password?: string
        },
    ): Promise<{ success: boolean; message: string }> {
        return request(this.config, {
            method: "POST",
            url: `admin/company/${customerId}/prodevis-test`,
            body: data,
        })
    }
}

export type WebserviceOption = {
    id: number
    name: string
    address: string | null
    description: string | null
}

export type CompanySettings = {
    id: number
    key: string | null
    name: string | null
    active: boolean
    color: string | null
    apikey: string | null
    database_server: string | null
    database_name: string | null
    webservice_id: number | null
    webservice: {
        id: number
        name: string
        address: string
        is_lightrest: boolean
    } | null
    ggl_place_id: string | null
    ggl_email: string | null
    fb_url: string | null
    team_id_mattermost: string | null
    /** F5 : mail au secretariat au retour d'intervention */
    intervention_mail_actif: boolean
    intervention_mail_destinataires: string | null
    prodevis_base: string | null
    prodevis_port: number | null
    prodevis_login: string | null
    prodevis_has_password: boolean
    lolapp_url: string | null
}

export type CompanySettingsInput = {
    key: string
    name: string
    active?: boolean
    color?: string | null
    apikey?: string
    database_server?: string | null
    database_name?: string | null
    webservice_id?: number | null
    ggl_place_id?: string | null
    ggl_email?: string | null
    fb_url?: string | null
    team_id_mattermost?: string | null
    intervention_mail_actif?: boolean
    intervention_mail_destinataires?: string | null
    prodevis_base?: string | null
    prodevis_port?: number | null
    prodevis_login?: string | null
    /** Absent ou vide : mot de passe enregistre conserve. */
    prodevis_password?: string | null
}

export const AdminLogic = new AdminLogicClass()
